import { useId, type ReactNode } from "react";

import { NavRow } from "./Rows";
import "./Fields.css";

/* Form primitives in the kit's field look (SNIPPETS §4 fields): a 12.8 label above a 48px box,
   the unit inside the box, one muted line under it. They live here rather than inside a page
   because the settings screens each render a slice of the same profile form. */

export function Field({
  label,
  type,
  value,
  onChange,
  step,
  min,
  max,
  hint,
  autoComplete,
  unit,
  placeholder,
  trailing,
  invalid,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  step?: string;
  min?: string;
  max?: string;
  /** one line under the box — what the number means, not how to type it */
  hint?: ReactNode;
  autoComplete?: string;
  /** "kg", "cm", "km/h" — inside the box, after the value */
  unit?: string;
  placeholder?: string;
  /** a 44px control at the end of the box, such as show-password */
  trailing?: ReactNode;
  invalid?: boolean;
}) {
  const id = useId();
  const hintId = useId();
  return (
    <div className={`k-field${invalid ? " k-field--invalid" : ""}`}>
      <label className="k-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="k-field__box">
        <input
          id={id}
          type={type}
          inputMode={type === "number" ? "decimal" : undefined}
          value={value}
          step={step}
          min={min}
          max={max}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={invalid || undefined}
          aria-describedby={hint ? hintId : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
        {unit ? <span className="k-field__unit">{unit}</span> : null}
        {trailing}
      </div>
      {hint ? (
        <p id={hintId} className="k-field__hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** A native select in the field box — the picker a phone draws is better than any we could. */
export function SelectField({
  label,
  value,
  onChange,
  options,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  hint?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="k-field">
      <label className="k-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="k-field__box">
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {hint ? <p className="k-field__hint">{hint}</p> : null}
    </div>
  );
}

/** A labelled multi-line box, for pasted JSON. */
export function AreaField({
  label,
  value,
  onChange,
  placeholder,
  rows = 6,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  const id = useId();
  return (
    <div className="k-field">
      <label className="k-field__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className="k-field__area"
        value={value}
        rows={rows}
        spellCheck={false}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** A label over a Segmented control, so a 2–3-way choice reads like the fields around it. */
export function ChoiceField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="k-field">
      <span className="k-field__label">{label}</span>
      {children}
    </div>
  );
}

/** Two (or three) fields side by side; collapses to two on a narrow phone. */
export function FieldGrid({ children, cols = 2 }: { children: ReactNode; cols?: 2 | 3 }) {
  return <div className={`k-fields__grid${cols === 3 ? " k-fields__grid--3" : ""}`}>{children}</div>;
}

/** One switch, as a single-row group — the kit's toggle row standing on its own. */
export function Toggle({
  title,
  sub,
  on,
  onChange,
}: {
  title: string;
  sub: ReactNode;
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="k-rows">
      <NavRow kind="toggle" title={title} subtitle={sub} checked={on} onChange={onChange} />
    </div>
  );
}

/** A heading and a stack of fields. The heading and the gap carry the grouping — no card. */
export function FieldSet({
  legend,
  hint,
  children,
}: {
  legend: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="k-group k-fields-group">
      <h2 className="k-group__h">{legend}</h2>
      <div className="k-fields">
        {children}
        {hint ? <p className="k-fields__hint">{hint}</p> : null}
      </div>
    </section>
  );
}
